import { type RuleCandidate } from '@components/Customization/PoiTypes/RuleEditor';
import { OPERATOR_VALUE_KIND, type RuleSubjectType } from '@lib/customization/poiRules';
import {
  newEntityId, type FormInputType, type PoiField, type PoiFieldCondition, type PoiFieldGroup, type PoiFieldGroupRule,
  type PoiFieldOption, type PoiFieldRule, type RuleOperator, type RuleTarget,
} from '@store/api/poiTypeEndpoints';

/**
 * The POI type designer's reading of a type's rules.
 *
 * Nothing here changes what is stored: rules stay `visible` plus `rules[]` on the dependent
 * entry, as `backend/doc/poi-field-rules.md` defines them. This module reads them as the
 * answer to "when is this asked?" and writes the common answer back in the same shape.
 *
 * The common answer is a *follow-up*: an entry asked only when another field has a certain
 * answer. Stored, that is the pair the contract describes: base `visible: false` and one rule
 * setting it true. Anything richer is `advanced` and stays a rule list.
 */

type Carrier = Pick<PoiField, 'visible'> & { rules?: (PoiFieldRule | PoiFieldGroupRule)[] };

/** When an entry is asked. */
export type Visibility =
  | { mode: 'always' }
  | { mode: 'never' }
  | {
    mode: 'when';
    /** Shown when the conditions hold, or hidden when they hold. */
    show: boolean;
    match: 'all' | 'any';
    conditions: PoiFieldCondition[];
    ruleId?: string;
  }
  | { mode: 'advanced' };

export type SimpleVisibility = Exclude<Visibility, { mode: 'advanced' }>;

/**
 * Reads an entry's rules as the simple builder would have written them, or `advanced`.
 *
 * Simple is no rules, or one rule that flips `visible` against the base. It may also turn
 * `required` on (what the old field drawer's "show when" wrote: the same as a required base,
 * since a hidden field is never enforced).
 */
export const readVisibility = (entry: Partial<Carrier>): Visibility => {
  const rules = entry.rules ?? [];
  const baseVisible = entry.visible !== false;

  if (!rules.length) return { mode: baseVisible ? 'always' : 'never' };
  if (rules.length > 1) return { mode: 'advanced' };

  const [{ id, when, then }] = rules;
  const flags = then as Partial<Record<RuleTarget, boolean>>;

  if (flags.visible === undefined || flags.visible === baseVisible) return { mode: 'advanced' };

  const onlySimpleExtras = Object.entries(flags).every(([key, value]) => value === undefined
    || key === 'visible'
    || (key === 'required' && value && flags.visible));

  if (!onlySimpleExtras) return { mode: 'advanced' };

  return { mode: 'when', show: flags.visible, match: when.match, conditions: when.conditions, ruleId: id };
};

/** Required as the author means it: by the base flag, or by the show-rule the old drawer wrote. */
export const readRequired = (field: PoiField): boolean =>
  field.required || (readVisibility(field).mode === 'when' && field.rules?.[0]?.then.required === true);

const ruleFor = <Then>(visibility: Extract<Visibility, { mode: 'when' }>, then: Then) => ({
  id: visibility.ruleId ?? newEntityId(),
  when: { match: visibility.match, conditions: visibility.conditions },
  then,
});

/**
 * Writes a simple visibility back as the stored pair. A `then.required` the old drawer wrote is
 * folded into the base flag on the way, which every client reads the same way. What a report
 * prints follows from the pair: a field is printed when it was asked.
 */
export const writeVisibility = (field: PoiField, visibility: SimpleVisibility, required: boolean): PoiField => (
  visibility.mode === 'when'
    ? { ...field, visible: !visibility.show, required, rules: [ruleFor(visibility, { visible: visibility.show })] }
    : { ...field, visible: visibility.mode === 'always', required, rules: undefined });

/** A group's rules set `visible` alone. */
export const writeGroupVisibility = (group: PoiFieldGroup, visibility: SimpleVisibility): PoiFieldGroup => (
  visibility.mode === 'when'
    ? { ...group, visible: !visibility.show, rules: [ruleFor(visibility, { visible: visibility.show })] }
    : { ...group, visible: visibility.mode === 'always', rules: undefined });

/**
 * The one field a simple "show when" points at, which is what lets the designer draw an entry
 * under that field. Null for anything else, conditions on two fields included.
 */
export const triggerOf = (entry: Partial<Carrier>): string | null => {
  const visibility = readVisibility(entry);

  if (visibility.mode !== 'when' || !visibility.show) return null;

  const ids = new Set(visibility.conditions.map((condition) => condition.fieldId));

  return ids.size === 1 ? [...ids][0] : null;
};

/** Every field id an entry's rules read. */
export const dependsOn = (entry: Partial<Carrier>): string[] =>
  [...new Set((entry.rules ?? []).flatMap((rule) => rule.when.conditions.map((condition) => condition.fieldId)))];

/** One field on the canvas, with the follow-ups drawn under it. */
export interface FieldNode {
  field: PoiField;
  /** Position in the field's own list, the order the device renders in. */
  index: number;
  children: FieldNode[];
}

/**
 * Nests follow-ups under their trigger without reordering anything: a field is drawn under
 * its trigger only when it sits right after it on the device, with nothing between them but
 * the trigger's other follow-ups. Anywhere else it stays at the top level.
 */
export const buildTree = (fields: PoiField[]): FieldNode[] => {
  const roots: FieldNode[] = [];
  const stack: FieldNode[] = [];

  fields.forEach((field, index) => {
    const node: FieldNode = { field, index, children: [] };
    const trigger = triggerOf(field);

    while (stack.length && stack[stack.length - 1].field.id !== trigger) stack.pop();

    (stack.length ? stack[stack.length - 1].children : roots).push(node);
    stack.push(node);
  });

  return roots;
};

const nodeAt = (nodes: FieldNode[], index: number): FieldNode | undefined => {
  for (const node of nodes) {
    if (node.index === index) return node;

    const found = nodeAt(node.children, index);

    if (found) return found;
  }

  return undefined;
};

const lastIndex = (node: FieldNode): number =>
  (node.children.length ? lastIndex(node.children[node.children.length - 1]) : node.index);

/** The index right after the field at `index` and everything drawn under it. */
export const blockEnd = (fields: PoiField[], index: number): number => {
  const node = nodeAt(buildTree(fields), index);

  return (node ? lastIndex(node) : index) + 1;
};

/** A List's `equals x` is its `isAnyOf [x]`; compared in that form, both are one answer. */
const answerKey = (conditions: PoiFieldCondition[]) => JSON.stringify(conditions.map(({ fieldId, operator, value }) =>
  (operator === 'equals' && typeof value === 'string'
    ? { fieldId, operator: 'isAnyOf', value: [value] }
    : { fieldId, operator, value })));

/**
 * Where a new follow-up of the field at `index` goes: after the follow-ups the same answer
 * already opens, so one answer's questions stay together; for an answer with none yet, in
 * the order of the trigger's choices; otherwise at the end of the trigger's block.
 */
export const followUpSlot = (fields: PoiField[], index: number, conditions: PoiFieldCondition[]): number => {
  const children = nodeAt(buildTree(fields), index)?.children ?? [];
  const conditionsOf = (child: FieldNode) => {
    const visibility = readVisibility(child.field);

    return visibility.mode === 'when' ? visibility.conditions : [];
  };

  const sameAnswer = children.filter((child) => answerKey(conditionsOf(child)) === answerKey(conditions));

  if (sameAnswer.length) return lastIndex(sameAnswer[sameAnswer.length - 1]) + 1;

  const choiceIds = (fields[index]?.options ?? []).map((option) => option.id);
  const rank = (value: PoiFieldCondition['value']) => {
    const position = choiceIds.indexOf((Array.isArray(value) ? value[0] : value) as string);

    return position < 0 ? Infinity : position;
  };
  const mine = rank(conditions[0]?.value);
  const laterChoice = children.find((child) => rank(conditionsOf(child)[0]?.value) > mine);

  return laterChoice && mine !== Infinity ? laterChoice.index : blockEnd(fields, index);
};

/** An answer a field can branch on, and the condition a follow-up of it carries. */
export interface FollowUpAnswer {
  key: string;
  condition: PoiFieldCondition;
  /** The choice it stands for, on a List or MultiList. */
  option?: PoiFieldOption;
}

/**
 * The answers worth branching on: each choice of a list, Yes and No of a toggle, a value above
 * the minimum of a number, a filled-in text. The condition is a starting point the author
 * refines in the builder.
 */
export const followUpAnswers = (field: PoiField): FollowUpAnswer[] => {
  const fieldId = field.id;

  if (!fieldId) return [];

  switch (field.type) {
    case 'List':
    case 'MultiList':
      return (field.options ?? []).flatMap((option) => (option.id
        ? [{ key: option.id, option, condition: { fieldId, operator: 'isAnyOf' as const, value: [option.id] } }]
        : []));
    case 'Toggle':
      return [
        { key: 'yes', condition: { fieldId, operator: 'equals', value: true } },
        { key: 'no', condition: { fieldId, operator: 'equals', value: false } },
      ];
    case 'Number':
    case 'Range':
      return [{ key: 'above', condition: { fieldId, operator: 'gt', value: field.min ?? 0 } }];
    default:
      return [{ key: 'filled', condition: { fieldId, operator: 'isNotEmpty' } }];
  }
};

/** Which of `followUpAnswers`' keys a follow-up's condition belongs to, for counting them. */
export const answerKeysOf = (condition: PoiFieldCondition, triggerType: FormInputType): string[] => {
  if (Array.isArray(condition.value)) return condition.value;
  if (typeof condition.value === 'boolean') return [condition.value ? 'yes' : 'no'];
  if (triggerType === 'List' && typeof condition.value === 'string') return [condition.value];

  return [triggerType === 'Number' || triggerType === 'Range' ? 'above' : 'filled'];
};

/**
 * The operators the builder offers per field type, in the order an author reaches for them.
 * A few contract operators are left out where another says the same more plainly (a List's
 * `equals` is a one-id `isAnyOf`); they are still read and described when they arrive. The
 * step's status takes the two the contract allows it.
 */
export const BUILDER_OPERATORS: Record<RuleSubjectType, RuleOperator[]> = {
  StepStatus: ['isAnyOf', 'isNoneOf'],
  List: ['isAnyOf', 'isNoneOf', 'isNotEmpty', 'isEmpty'],
  MultiList: ['isAnyOf', 'isNoneOf', 'equals', 'countGt', 'countLt', 'countEquals', 'isNotEmpty', 'isEmpty'],
  Toggle: ['equals'],
  Number: ['gt', 'gte', 'lt', 'lte', 'equals', 'notEquals', 'isNotEmpty', 'isEmpty'],
  Range: ['gt', 'gte', 'lt', 'lte', 'equals', 'notEquals'],
  Text: ['contains', 'notContains', 'equals', 'notEquals', 'isNotEmpty', 'isEmpty'],
  Barcode: ['contains', 'notContains', 'equals', 'notEquals', 'isNotEmpty', 'isEmpty'],
};

/**
 * A condition in the form the builder offers it, where the contract has two ways to say the
 * same thing: a List's `equals x` is `isAnyOf [x]` (and `notEquals` is `isNoneOf`), a
 * Toggle's `notEquals v` is `equals !v`. Shown and described that way; nothing is rewritten
 * until the author edits the condition.
 */
export const asBuilderCondition = (condition: PoiFieldCondition, candidate?: RuleCandidate): PoiFieldCondition => {
  const { operator, value } = condition;

  if (candidate?.type === 'Toggle' && operator === 'notEquals') return { ...condition, operator: 'equals', value: !value };
  if (candidate?.type !== 'List' || typeof value !== 'string' || !value) return condition;
  if (operator === 'equals') return { ...condition, operator: 'isAnyOf', value: [value] };
  if (operator === 'notEquals') return { ...condition, operator: 'isNoneOf', value: [value] };

  return condition;
};

/** A fresh condition on a field, answered with something sensible so no value box starts empty. */
export const defaultCondition = (candidate: RuleCandidate): PoiFieldCondition => {
  switch (candidate.type) {
    case 'List':
    case 'MultiList':
    case 'StepStatus':
      return { fieldId: candidate.id, operator: 'isAnyOf', value: [] };
    case 'Toggle':
      return { fieldId: candidate.id, operator: 'equals', value: true };
    case 'Number':
    case 'Range':
      return { fieldId: candidate.id, operator: 'gt', value: candidate.min ?? 0 };
    default:
      return { fieldId: candidate.id, operator: 'isNotEmpty' };
  }
};

/** A condition's value after its operator changes: kept if the kind of value is the same. */
export const valueForOperator = (
  condition: PoiFieldCondition, operator: RuleOperator, candidate: RuleCandidate,
): PoiFieldCondition['value'] => {
  const before = OPERATOR_VALUE_KIND[condition.operator];
  const after = OPERATOR_VALUE_KIND[operator];

  if (after === 'none') return undefined;
  if (before === after) return condition.value;
  if (after === 'count') return 1;
  if (after === 'text') return '';
  if (after === 'list' || candidate.type === 'MultiList') return Array.isArray(condition.value) ? condition.value : [];
  if (after === 'number' || candidate.type === 'Number' || candidate.type === 'Range') return candidate.min ?? 0;

  return candidate.type === 'Toggle' ? true : '';
};

